/**
 * Shows a banner when the site's documents are held read-only, whatever the cause.
 *
 * Read-only is one state to the person looking at it, so it gets one banner: the same
 * placement, the same style, and the same shape of sentence, with only the explanation and
 * the remedy varying by cause. The causes themselves own their warnings, which is a
 * different job: those say a site is heading for trouble, and stop where this starts.
 *
 * The cause is org.readOnlyReason, decided by the server method that does the holding, so
 * the banner cannot say something other than what is enforced.
 *
 * Listed by the saas and enterprise banner sets, not the core one. A core installation can be
 * held read-only, but in practice is not, and an enterprise one says so through its activation
 * banner instead. Kept out of grist-core for that reason, and because the remedies here are
 * for a subscription that only the hosted service sells.
 *
 * Each cause keeps its own remedy, which is expected to move under the plan it belongs to when
 * subscription states are restructured. The single banner is about what the reader sees, not
 * about where the explanations should live.
 *
 * Shown to everyone with access to the site, whatever their role. Site role does not say who
 * is affected, since documents and workspaces are shared across it: someone shared into a
 * single document is a guest of the site, and loses write access along with everyone else.
 * Why it is read-only, though, is only said to owners and billing managers, who are the ones
 * with something to do about it.
 */

import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { buildUpgradeMessage } from "app/client/components/DocumentUsage";
import { makeT } from "app/client/lib/localization";
import { inlineMarkdown } from "app/client/lib/markdown";
import { AppModel } from "app/client/models/AppModel";
import { urlState } from "app/client/models/gristUrlState";
import { canUpgradeOrg } from "app/common/roles";
import { SiteReadOnlyReason } from "app/common/UserAPI";

import { Disposable, dom, DomElementArg, makeTestId } from "grainjs";

const t = makeT("SiteReadOnlyBanner");

const testId = makeTestId("test-site-read-only-banner-");

export class SiteReadOnlyBanner extends Disposable {
  private readonly _currentOrg = this._app.currentOrg;
  private readonly _billingAccount = this._currentOrg?.billingAccount;

  // Whether the reader is someone who can lift the state themselves, rather than someone who
  // has to ask. Billing managers can, without necessarily having any access to the site.
  private readonly _canAct = this._app.isOwner() || Boolean(this._billingAccount?.isManager);

  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    const reason = this._currentOrg?.readOnlyReason;
    if (!reason) { return null; }

    return dom.create(Banner, {
      content: buildBannerMessage(
        ...this._buildMessage(reason),
        testId("text"),
        // Hidden, for tests to read the cause without matching on wording.
        dom("input", { type: "hidden", value: reason }, testId("reason")),
      ),
      style: "error",
      showCloseButton: false,
      showExpandButton: true,
    });
  }

  private _buildMessage(reason: SiteReadOnlyReason): DomElementArg[] {
    // Why a site is read-only is billing detail, and goes to the people who can act on it, as
    // the member count does. Everyone else is told what changed for them, and who to ask.
    if (!this._canAct) {
      return [t("Your site is in read-only mode. Please contact the site owner.")];
    }

    const billingUrl = urlState().makeUrl({ billing: "billing" });
    switch (reason) {
      case "suspended":
        return [inlineMarkdown(t("Your site is in read-only mode because its subscription has \
been cancelled. To reactivate your subscription, please [renew your plan]({{billingUrl}}).",
        { billingUrl }))];

      case "plan":
        return [t("Your site is in read-only mode. Please contact support for assistance.")];

      case "billing":
        return [inlineMarkdown(this._buildBillingMessage(billingUrl))];

      case "users":
        return [
          t("Your site is in read-only mode because it is over the free plan limit of \
{{count}} team members.", { count: this._billingAccount?.features?.maxUsersPerOrg }),
          " ",
          buildUpgradeMessage(canUpgradeOrg(this._currentOrg), "long", () => this._app.showUpgradeModal()),
        ];
    }
  }

  // The remedy for a subscription differs by how far it has gone, so the wording follows the
  // stripe status, as it did when these messages lived with the subscription warnings.
  private _buildBillingMessage(billingUrl: string): string {
    const status = this._billingAccount?.status?.stripeStatus;
    if (status === "unpaid") {
      return t("Your site is in read-only mode because your team's subscription plan is more \
than 30-days past due. To reactivate your subscription, please \
[pay the latest invoice]({{billingUrl}}).", { billingUrl });
    }
    if (status === "canceled") {
      return t("Your site is in read-only mode because your team's subscription plan has been \
cancelled. To reactivate your subscription, please [renew your plan]({{billingUrl}}).", { billingUrl });
    }
    return t("Your site is in read-only mode because your subscription is not in good standing. \
Please [pay the latest invoice]({{billingUrl}}) to reactivate your subscription.", { billingUrl });
  }
}
